import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toEPSG25830 } from "../lib/projection";
import { useT } from "../lib/i18n";
import { Play, Pause } from "lucide-react";

const SPAIN_CENTER = [39.8628, -3.9273];
const SPAIN_ZOOM = 6;

const STYLES = {
  flood: { color: "#DC2626", weight: 1.2, fillColor: "#EF4444", fillOpacity: 0.35 },
  roads: { color: "#38BDF8", weight: 1.8, opacity: 0.85 },
};
const POINT_COLORS = { bridges: "#F59E0B", ports: "#A855F7", airstrips: "#10B981" };
const SAIH_COLORS = { normal: "#10B981", prealert: "#F59E0B", alert: "#EF4444", emergency: "#7F1D1D" };

function pointLayer(data, color) {
  return L.geoJSON(data, {
    pointToLayer: (f, latlng) =>
      L.circleMarker(latlng, {
        radius: 5,
        color,
        weight: 1.5,
        fillColor: color,
        fillOpacity: 0.55,
      }),
    onEachFeature: (f, layer) => {
      const n = f.properties?.nombre || f.properties?.name || "—";
      layer.bindPopup(`<div class="resilio-popup">${n}</div>`);
    },
  });
}

export const MapStage = ({ geo, visibility, focus, gpmConfig, alerts, children }) => {
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const layerRefs = useRef({});
  const tileRefs = useRef({});
  const alertLayerRef = useRef(null);
  const resetBtnRef = useRef(null);
  const resetTitleRef = useRef("Reset view");
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const t = useT();
  const [coord, setCoord] = useState(null);
  const [plumeHour, setPlumeHour] = useState(0);
  const [playing, setPlaying] = useState(false);

  // init map once
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, {
      center: SPAIN_CENTER,
      zoom: SPAIN_ZOOM,
      zoomControl: false,
      attributionControl: true,
      preferCanvas: true,
    });
    L.tileLayer(
      `${BACKEND}/api/geo/basemap/{z}/{y}/{x}.png`,
      { attribution: "Esri · HERE · Garmin · © OpenStreetMap", maxZoom: 16 }
    ).addTo(map);
    L.tileLayer(
      `${BACKEND}/api/geo/basemap-ref/{z}/{y}/{x}.png`,
      { maxZoom: 16, opacity: 0.6 }
    ).addTo(map);
    L.control.zoom({ position: "bottomright" }).addTo(map);
    const resetCtl = L.control({ position: "bottomright" });
    resetCtl.onAdd = () => {
      const btn = L.DomUtil.create("button", "resilio-reset-btn");
      btn.type = "button";
      btn.innerHTML = "⌖";
      btn.title = resetTitleRef.current;
      btn.setAttribute("data-testid", "map-reset-view");
      Object.assign(btn.style, {
        width: "34px", height: "34px", display: "grid", placeItems: "center",
        background: "#0B0F19", color: "#38BDF8", border: "1px solid #1e293b",
        borderRadius: "6px", fontSize: "16px", cursor: "pointer", marginTop: "6px",
        boxShadow: "0 2px 8px rgba(0,0,0,.4)",
      });
      L.DomEvent.disableClickPropagation(btn);
      L.DomEvent.on(btn, "click", () => map.setView(SPAIN_CENTER, SPAIN_ZOOM));
      resetBtnRef.current = btn;
      return btn;
    };
    resetCtl.addTo(map);
    setTimeout(() => map.invalidateSize(), 200);
    map.on("mousemove", (e) => {
      const { x, y } = toEPSG25830(e.latlng.lat, e.latlng.lng);
      setCoord({ lat: e.latlng.lat, lng: e.latlng.lng, x, y });
    });
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // build layers when data arrives
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !geo) return;
    Object.values(layerRefs.current).forEach((l) => l && map.removeLayer(l));
    layerRefs.current = {};

    if (geo.flood) layerRefs.current.flood = L.geoJSON(geo.flood, { style: STYLES.flood });
    if (geo.roads) layerRefs.current.roads = L.geoJSON(geo.roads, { style: STYLES.roads });
    ["bridges", "ports", "airstrips"].forEach((k) => {
      if (geo[k]) layerRefs.current[k] = pointLayer(geo[k], POINT_COLORS[k]);
    });
    if (geo.saih) layerRefs.current.saih = L.geoJSON(geo.saih, {
      pointToLayer: (f, latlng) => {
        const c = SAIH_COLORS[f.properties.status] || "#10B981";
        return L.circleMarker(latlng, { radius: 6, color: c, weight: 2, fillColor: c, fillOpacity: 0.85 });
      },
      onEachFeature: (f, layer) => layer.bindPopup(
        `<div class="resilio-popup"><b>SAIH ${f.properties.code}</b> ${f.properties.feed_live ? '<span style="color:#34d399">● EN VIVO</span>' : '<span style="color:#f59e0b">● estimación</span>'}<br/>${f.properties.river} · ${f.properties.name}<br/>${f.properties.nivel} · ${f.properties.status.toUpperCase()}${f.properties.thresholds ? `<br/><span style="color:#7dd3fc">CHJ ${f.properties.thresholds.low}/${f.properties.thresholds.medium}/${f.properties.thresholds.high} m³/s · T100 ${f.properties.thresholds.qt100}</span>` : ""}<br/><span style="color:#64748b">${f.properties.source}</span></div>`),
    });
    if (geo.ems) layerRefs.current.ems = L.geoJSON(geo.ems, {
      pointToLayer: (f, latlng) => L.circleMarker(latlng, { radius: 10, color: "#FB923C", weight: 2, fillColor: "#FB923C", fillOpacity: 0.25 }),
      onEachFeature: (f, layer) => layer.bindPopup(
        `<div class="resilio-popup"><b>Copernicus ${f.properties.code}</b><br/>${f.properties.title}<br/>${f.properties.event} · ${f.properties.status}</div>`),
    });
    // apply current visibility
    Object.entries(layerRefs.current).forEach(([k, l]) => {
      if (visibility[k]) l.addTo(map);
    });
  }, [geo]); // eslint-disable-line react-hooks/exhaustive-deps

  // toggle visibility (vector layers + satellite tile overlays)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const all = { ...layerRefs.current, ...tileRefs.current };
    Object.entries(all).forEach(([k, l]) => {
      if (!l) return;
      if (visibility[k] && !map.hasLayer(l)) l.addTo(map);
      if (!visibility[k] && map.hasLayer(l)) map.removeLayer(l);
    });
  }, [visibility]);

  // NASA GPM (GIBS IMERG) precipitation heatmap overlay
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !gpmConfig) return;
    const layer = L.tileLayer(`${BACKEND}${gpmConfig.tile_url}`, {
      opacity: 0.6,
      maxNativeZoom: gpmConfig.max_native_zoom || 6,
      zIndex: 350,
      attribution: gpmConfig.attribution,
    });
    tileRefs.current.gpm = layer;
    if (visibility.gpm) layer.addTo(map);
    return () => {
      if (map.hasLayer(layer)) map.removeLayer(layer);
      delete tileRefs.current.gpm;
    };
  }, [gpmConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  // Copernicus Sentinel-3 OLCI soil-saturation overlay (backend proxy)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const layer = L.tileLayer(`${BACKEND}/api/geo/sentinel/{z}/{x}/{y}.png`, {
      opacity: 0.55,
      minNativeZoom: 7,
      maxNativeZoom: 9,
      bounds: [[35.9, -9.5], [43.9, 4.5]],
      zIndex: 340,
      attribution: "Copernicus Sentinel-3 OLCI · CDSE",
    });
    tileRefs.current.sentinel = layer;
    if (visibility.sentinel) layer.addTo(map);
    return () => {
      if (map.hasLayer(layer)) map.removeLayer(layer);
      delete tileRefs.current.sentinel;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // AEMET national radar overlay (refreshes every 10 min)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const BOUNDS = [[27.64, -18.16], [43.79, 4.32]];
    let layer;
    const build = () => {
      if (layer && map.hasLayer(layer)) map.removeLayer(layer);
      layer = L.imageOverlay(`${BACKEND}/api/geo/radar?t=${Date.now()}`, BOUNDS, { opacity: 0.55, zIndex: 345 });
      tileRefs.current.radar = layer;
      if (visibility.radar) layer.addTo(map);
    };
    build();
    const iv = setInterval(build, 600000);
    return () => {
      clearInterval(iv);
      if (layer && map.hasLayer(layer)) map.removeLayer(layer);
      delete tileRefs.current.radar;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // keep reset-view tooltip localized
  useEffect(() => {
    resetTitleRef.current = t.reset_view;
    if (resetBtnRef.current) resetBtnRef.current.title = t.reset_view;
  }, [t]);

  // confidence-uncertainty halos + forecast precipitation plume
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (alertLayerRef.current) map.removeLayer(alertLayerRef.current);
    const group = L.layerGroup();
    const sevColor = (s) => (s === "critical" ? "#EF4444" : s === "warning" ? "#F59E0B" : s === "nodata" ? "#64748B" : "#10B981");
    (alerts || []).forEach((a) => {
      if (!a.coords) return;
      const color = sevColor(a.severity);
      const conf = typeof a.confidence === "number" ? a.confidence : 70;
      // uncertainty halo: lower confidence -> larger, softer radius (~2km..25km)
      const haloR = 2000 + (100 - conf) * 230;
      L.circle(a.coords, { radius: haloR, color, weight: 0, fillColor: color, fillOpacity: 0.07 }).addTo(group);
      L.circle(a.coords, { radius: haloR * 0.55, color, weight: 0.6, opacity: 0.4, fillColor: color, fillOpacity: 0.1 }).addTo(group);
      L.circleMarker(a.coords, { radius: 5, color, weight: 1.5, fillColor: color, fillOpacity: 0.95 })
        .bindPopup(`<div class="resilio-popup"><b>${a.municipio || "—"}</b><br/>${a.kind === "forecast" ? "Pronóstico · " : ""}${a.variance_pct != null ? "Δ+" + a.variance_pct + "%" : ""}<br/>${t.confianza || "Confianza"} ${conf}%</div>`)
        .addTo(group);
      // forecast plume grows with the temporal slider
      if (a.kind === "forecast" && Array.isArray(a.series_hourly) && plumeHour > 0) {
        const cum = a.series_hourly[Math.min(plumeHour, a.series_hourly.length) - 1] || 0;
        const plumeR = Math.min(55000, 3000 + cum * 2500);
        L.circle(a.coords, { radius: plumeR, color, weight: 1, opacity: 0.5, fillColor: color, fillOpacity: 0.14 }).addTo(group);
      }
    });
    group.addTo(map);
    alertLayerRef.current = group;
    return () => {
      if (alertLayerRef.current && map.hasLayer(alertLayerRef.current)) map.removeLayer(alertLayerRef.current);
    };
  }, [alerts, plumeHour]); // eslint-disable-line react-hooks/exhaustive-deps

  // playback animation
  useEffect(() => {
    if (!playing) return;
    const iv = setInterval(() => {
      setPlumeHour((h) => {
        if (h >= 6) { setPlaying(false); return 6; }
        return h + 1;
      });
    }, 1100);
    return () => clearInterval(iv);
  }, [playing]);

  // focus on alert
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !focus) return;
    const [lat, lng] = focus.coords;
    map.flyTo([lat, lng], 11, { duration: 1.1 });
    const marker = L.circleMarker([lat, lng], {
      radius: 14,
      color: "#38BDF8",
      weight: 2,
      fillColor: "#38BDF8",
      fillOpacity: 0.15,
      className: "resilio-focus-pulse",
    }).addTo(map);
    marker.bindPopup(`<div class="resilio-popup"><b>${focus.titulo}</b><br/>${focus.municipio} · ${focus.return_period}</div>`).openPopup();
    const t = setTimeout(() => map.removeLayer(marker), 6000);
    return () => {
      clearTimeout(t);
      if (map.hasLayer(marker)) map.removeLayer(marker);
    };
  }, [focus]);

  return (
    <div className="flex-1 relative overflow-hidden" data-testid="map-container">
      <div ref={containerRef} className="absolute inset-0" data-testid="leaflet-map-canvas" />
      {children}
      {(alerts || []).some((a) => a.kind === "forecast" && a.series_hourly) && (
        <div
          className="absolute bottom-3 left-1/2 -translate-x-1/2 z-[1000] w-[min(560px,70%)] bg-[#0B0F19]/92 backdrop-blur-md border border-slate-800 rounded-lg px-4 py-2.5 shadow-2xl"
          data-testid="map-time-slider"
        >
          <div className="flex items-center gap-3">
            <button
              onClick={() => setPlaying((p) => !p)}
              data-testid="map-time-play"
              title={playing ? t.pause : t.play}
              className="shrink-0 grid place-items-center h-7 w-7 rounded bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/25 transition-colors"
            >
              {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
            </button>
            <input
              type="range" min="0" max="6" step="1" value={plumeHour}
              onChange={(e) => { setPlaying(false); setPlumeHour(Number(e.target.value)); }}
              data-testid="map-time-range"
              className="flex-1 accent-cyan-400 cursor-pointer"
            />
            <span className="shrink-0 font-mono text-[11px] text-cyan-300 tabular-nums w-16 text-right">
              {plumeHour === 0 ? t.time_now : `+${plumeHour} h`}
            </span>
          </div>
          <div className="flex items-center justify-between mt-1 font-mono text-[9px] text-slate-500">
            <span>{t.time_playback}</span>
            <span className="text-amber-400/80">{t.plume_legend}</span>
          </div>
        </div>
      )}
      <div
        className="absolute bottom-3 left-3 z-[1000] bg-[#0B0F19]/90 backdrop-blur-md border border-slate-800 rounded-lg px-3 py-2 font-mono text-[10px] text-slate-400 shadow-2xl"
        data-testid="coordinate-readout"
      >
        <div className="text-cyan-400 tracking-wider mb-1">EPSG:25830 · ETRS89 UTM 30N</div>
        {coord ? (
          <div className="space-y-0.5 tabular-nums">
            <div>E: <span className="text-slate-200">{coord.x.toFixed(1)} m</span></div>
            <div>N: <span className="text-slate-200">{coord.y.toFixed(1)} m</span></div>
            <div className="text-slate-600">
              WGS84 {coord.lat.toFixed(4)}, {coord.lng.toFixed(4)}
            </div>
          </div>
        ) : (
          <div className="text-slate-600">{t.coord_hint}</div>
        )}
      </div>
    </div>
  );
};
